import { CheckCircle2, Circle, CircleDot } from "lucide-react";
import { NavLink } from "react-router-dom";
import { currentStage, runName, runStages, type RunStage } from "../lib/runFlow";
import type { RunStatus } from "../lib/types";
import { StatusPill } from "./primitives";

/**
 * THIS RUN — the group that only exists when a run is open, and that NAMES the run.
 *
 * It answers, in one object, the two questions the flat seven-tab nav could not:
 *
 *     WHICH run am I in?     The organization, and the run's real status, at the top.
 *     WHERE is it up to?     Draft -> Review -> Package, with the stage the run is
 *                            actually waiting at marked, and every other stage stating
 *                            what has or has not happened to it.
 *
 * Draft and Review are LINKS, because they are the two run-scoped surfaces the spec
 * defines. Package is NOT a link and never becomes one: there is no package route — the
 * export happens on the review screen once QA has cleared — and a link to a surface that
 * does not exist would be a lie about the shape of the product.
 *
 * TWO MARKS, TWO MEANINGS, AND THEY ARE DELIBERATELY DIFFERENT CHANNELS:
 *   - "You are here" is the ROUTE you are looking at. It is the NavLink's active state, the
 *     same accent treatment every other destination in the chrome uses.
 *   - "The run is here" is where the RUN is waiting, derived in lib/runFlow.ts from the
 *     decisions made and the QA verdict returned. It is the step's own state.
 * They coincide most of the time. When they do not — you are reading the draft while three
 * edits sit undecided — the operator can see both facts, which is the entire point.
 *
 * State is carried by GLYPH + WORDS + COLOUR together, never by colour alone, and never by
 * a coloured bar down one edge of anything.
 */

const STAGE_GLYPH = {
  done: CheckCircle2,
  current: CircleDot,
  waiting: Circle
};

/** What the step's state means, spelled out for a screen reader and for a hover. */
const STAGE_STATE_LABEL = {
  done: "Done",
  current: "This run is here",
  waiting: "Not started"
};

function StageBody({ stage, active }: { stage: RunStage; active: boolean }) {
  const Glyph = STAGE_GLYPH[stage.state];
  return (
    <>
      <span className="stage-num" aria-hidden="true">
        {stage.step}
      </span>
      {stage.to ? (
        <span className="nav-item-label" data-testid="nav-label" data-active={active ? "true" : "false"}>
          {stage.label}
        </span>
      ) : (
        <span className="nav-item-label">{stage.label}</span>
      )}
      {/* WHAT HAS ACTUALLY HAPPENED HERE. Glyph, words and colour together — never colour
          alone, and never a claim: "After QA passes" is not "ready", and it never becomes
          "ready" until the backend says a check passed. */}
      <span className="stage-status">
        <Glyph aria-hidden="true" />
        {stage.status}
      </span>
      {/* The one line saying what the stage IS. It carries `nav-item-desc` because it is the
          same contract every other destination in the chrome honours — every nav item says
          what it shows — and the stage is a destination like any other. Painted in the menu,
          where there is room for it; the stepper under the header is the same DOM with this
          line not drawn. */}
      <span className="nav-item-desc stage-purpose">{stage.purpose}</span>
      <span className="sr-only">{STAGE_STATE_LABEL[stage.state]}</span>
    </>
  );
}

function Stage({ stage }: { stage: RunStage }) {
  if (!stage.to) {
    return (
      <li className="stage-item">
        <span className="stage" data-state={stage.state} data-stage={stage.id}>
          <StageBody stage={stage} active={false} />
        </span>
      </li>
    );
  }
  return (
    <li className="stage-item">
      <NavLink
        className={({ isActive }) => `stage ${isActive ? "active" : ""}`}
        to={stage.to}
        data-testid="nav-link"
        data-nav={stage.label}
        data-state={stage.state}
        data-stage={stage.id}
      >
        {({ isActive }) => <StageBody stage={stage} active={isActive} />}
      </NavLink>
    </li>
  );
}

/**
 * The run's identity and its flow. ONE component, rendered in both places the nav lives:
 * pinned under the chrome on the run screens (every viewport), and inside the menu sheet,
 * which IS the nav on a phone. The same DOM, so the two can never drift apart.
 */
export function RunFlow({ run, className }: { run: RunStatus; className?: string }) {
  const stages = runStages(run);
  const here = currentStage(stages);

  return (
    <section className={`run-flow ${className || ""}`} data-testid="run-flow" aria-label="This run">
      <div className="run-flow-head">
        <p className="run-flow-eyebrow">This run</p>
        <h2 className="run-flow-name" data-testid="run-flow-name">
          {runName(run)}
        </h2>
        <StatusPill status={run.status} />
      </div>

      {/* An ordered list, because it is one: the stages happen in this order and only this
          order. The connector between the steps is a NEUTRAL hairline — a coloured strip
          down the edge of anything is banned in this app, and a flow line is structure, not
          state. */}
      <ol className="run-flow-steps" data-testid="run-flow-steps">
        {stages.map((stage) => (
          <Stage stage={stage} key={stage.id} />
        ))}
      </ol>

      {/* WHAT TO DO, ONCE, IN PLAIN ENGLISH — from the stage the run is actually at. It is
          not a status; it is the purpose of the step the run is waiting on. */}
      <p className="run-flow-now" data-testid="run-flow-now">
        <strong>{here.label}</strong> {here.purpose}
      </p>
    </section>
  );
}
